import { inject, Injectable } from '@angular/core';

import { Apollo, QueryRef } from 'apollo-angular';
import { Observable, of, zip } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

import {
  Attachment,
  CancelAppointmentData,
  DeclareIntentData,
  PropertySearcherDocumentTypes,
  ReasonData
} from '@ui/shared/model';
import { FileUploadService } from '@ui/legacy-lib';
import { UserData } from '@ui/tenant-pool/shared/user/model';
import {
  acceptAppointmentMutation,
  applicationRegisterMutation,
  appointmentAcceptancesCancelMutation,
  brandingQuery,
  customQuestionResponsesQuery,
  declareIntentMutation,
  deleteQuery,
  guestApplicationQuery,
  GuestApplicationQueryResult,
  guestAppointmentsQuery,
  GuestAppointmentsQueryResult,
  guestQuestionsQuery,
  GuestQuestionsQueryResponse,
  registerMutation
} from '../queries/guest-mode.queries';

@Injectable({
  providedIn: 'root'
})
export class GuestModeFacade {
  private apollo = inject(Apollo);
  private fileUploadService = inject(FileUploadService);

  private appointmentsQuery: QueryRef<GuestAppointmentsQueryResult>;
  private applicationQuery: QueryRef<GuestApplicationQueryResult>;

  public loadGuestAppointments(token: string) {
    this.appointmentsQuery =
      this.apollo.watchQuery<GuestAppointmentsQueryResult>({
        query: guestAppointmentsQuery,
        variables: { token },
        fetchPolicy: 'no-cache'
      });

    return this.appointmentsQuery.valueChanges.pipe(
      filter(response => response.dataState === 'complete'),
      map(response => response.data.guestAppointments)
    );
  }

  public loadGuestApplication(token: string) {
    this.applicationQuery = this.apollo.watchQuery<GuestApplicationQueryResult>(
      {
        query: guestApplicationQuery,
        variables: { token },
        fetchPolicy: 'no-cache'
      }
    );

    return this.applicationQuery.valueChanges.pipe(
      filter(response => response.dataState === 'complete'),
      map(res => res.data.guestApplication)
    );
  }

  public loadCustomQuestionResponses(token: string) {
    return this.apollo
      .query({
        query: customQuestionResponsesQuery,
        variables: { token },
        fetchPolicy: 'no-cache'
      })
      .pipe(map(res => res.data.customQuestionResponses));
  }

  public loadBranding(token: string) {
    return this.apollo
      .query({
        query: brandingQuery,
        variables: { token },
        fetchPolicy: 'no-cache'
      })
      .pipe(map(res => res.data.branding));
  }

  public applicationRegister(
    tenantData: UserData,
    token: string,
    captchaResponse?: string
  ) {
    return this.uploadUserFiles(tenantData.userProfileData, token).pipe(
      map(userProfileData => ({ ...tenantData, userProfileData })),
      switchMap(userData =>
        this.apollo.mutate({
          mutation: applicationRegisterMutation,
          variables: { userData, token, captchaResponse }
        })
      )
    );
  }

  public register(userData: UserData, token: string, captchaResponse?: string) {
    return this.apollo.mutate({
      mutation: registerMutation,
      variables: { userData, token, captchaResponse }
    });
  }

  public delete(reasonData: ReasonData, token: string) {
    return this.apollo.mutate({
      mutation: deleteQuery,
      variables: { token, reasonData }
    });
  }

  public acceptAppointment(id: string, token: string) {
    return this.apollo.mutate({
      mutation: acceptAppointmentMutation,
      variables: { id, token },
      update: () => {
        this.refetchAppointments();
        this.refetchApplication();
      }
    });
  }

  public cancelAppointment(
    cancelAppointmentData: CancelAppointmentData,
    token: string
  ) {
    return this.apollo.mutate({
      mutation: appointmentAcceptancesCancelMutation,
      variables: { cancelAppointmentData, token },
      update: () => {
        this.refetchAppointments();
        this.refetchApplication();
      }
    });
  }

  public declareIntent(declareIntentData: DeclareIntentData, token: string) {
    return this.apollo.mutate({
      mutation: declareIntentMutation,
      variables: { declareIntentData, token },
      update: () => this.refetchApplication()
    });
  }

  public loadGuestQuestions(token: string) {
    return this.apollo
      .query<GuestQuestionsQueryResponse>({
        query: guestQuestionsQuery,
        variables: { token }
      })
      .pipe(map(response => response.data.guestQuestions));
  }

  private refetchAppointments(): void {
    if (this.appointmentsQuery) {
      void this.appointmentsQuery.refetch();
    }
  }

  private refetchApplication(): void {
    if (this.applicationQuery) {
      void this.applicationQuery.refetch();
    }
  }

  private uploadUserFiles(userProfileData, token: string): Observable<any> {
    const {
      portrait,
      creditScore,
      wbsDocument,
      incomeProof,
      otherDocuments,
      certificateOfStudy
    } = userProfileData || {};
    return zip(
      this.uploadFile(portrait, PropertySearcherDocumentTypes.IMG, token),
      this.uploadFiles(
        creditScore,
        PropertySearcherDocumentTypes.CREDIT_REPORT,
        token
      ),
      this.uploadFiles(
        wbsDocument,
        PropertySearcherDocumentTypes.WB_CERTIFICATE,
        token
      ),
      this.uploadFiles(
        incomeProof,
        PropertySearcherDocumentTypes.INCOME_STATEMENT,
        token
      ),
      this.uploadFiles(
        otherDocuments,
        PropertySearcherDocumentTypes.ADDITIONAL_DOCUMENTS,
        token
      ),
      this.uploadFiles(
        certificateOfStudy,
        PropertySearcherDocumentTypes.CERTIFICATE_OF_STUDY,
        token
      )
    ).pipe(
      map(
        ([
          uploadedPortrait,
          uploadedCreditScore,
          uploadedWbs,
          uploadedIncomeProof,
          uploadedOtherDocuments,
          uploadedCertificateOfStudy
        ]) => {
          const documentsToSave = [
            ...uploadedCreditScore,
            ...uploadedWbs,
            ...uploadedIncomeProof,
            ...uploadedOtherDocuments,
            ...uploadedCertificateOfStudy
          ].filter(
            attachment => attachment && !(attachment.file instanceof Blob)
          );
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return {
            ...this.getCleanProfile(userProfileData),
            portrait: uploadedPortrait.length > 0 ? uploadedPortrait[0] : null,
            attachments: [...documentsToSave]
          };
        }
      )
    );
  }

  private getCleanProfile(profileData) {
    const {
      wbsDocument,
      creditScore,
      incomeProof,
      otherDocuments,
      certificateOfStudy,
      ...restOfProfile
    } = profileData || {};
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return restOfProfile;
  }

  private uploadFiles(
    attachments: Attachment[] = [],
    type: PropertySearcherDocumentTypes,
    token: string
  ): Observable<Attachment[]> {
    const toUpload = (attachments || []).filter(
      attachment => attachment.file instanceof Blob
    );
    if (!toUpload.length) return of([...(attachments || [])]);

    const files = toUpload.map(attachment => attachment.file) as File[];
    const filesType = PropertySearcherDocumentTypes.SHARED_DOCUMENT;

    return this.fileUploadService.uploadFileWithToken(
      files,
      filesType,
      type,
      token
    );
  }

  private uploadFile(
    attachment: Attachment,
    type: PropertySearcherDocumentTypes,
    token: string
  ): Observable<Attachment[]> {
    const file = (attachment || {}).file;
    if (!(file instanceof File)) return of([attachment]);

    return this.fileUploadService.uploadFileWithToken(
      [file],
      type,
      type,
      token
    );
  }
}
